/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 그리드의 숫자 필터 클래스를 정의한다.
 *
 * @file /src/models/table/filters/Number.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 5. 17.
 */
import * as parent from './Filter';
import { Number as NumberField } from '@components/form/field/number/Number';
import { Select } from '@components/form/field/select/Select';
import { Container } from '@components/form/field/container/Container';
import { Local } from '@models/store/list/Local';
import { Language } from '@utils/Language';

export interface Properties extends parent.Properties {
    /**
     * 필터타입
     */
    type: 'number';

    /**
     * 완전일치여부 (true 이면 operator 선택 없이 = 만 사용)
     */
    is_equal?: boolean;

    /**
     * 스피너 표시여부
     */
    spinner?: boolean;

    /**
     * 스피너 단위
     */
    step?: number;

    /**
     * 숫자 포맷 적용여부
     */
    format?: boolean;
}

export class Number extends parent.Filter {
    /**
     * 필터타입
     */
    type: string = 'number';

    /**
     * 완전일치여부
     */
    is_equal: boolean;

    /**
     * 스피너 표시여부
     */
    spinner: boolean;

    /**
     * 스피너 단위
     */
    step: number;

    /**
     * 숫자 포맷 적용여부
     */
    format: boolean;

    /**
     * 비교연산자 필드
     */
    operatorField: Select = null;

    /**
     * 값 필드
     */
    valueField: NumberField = null;

    /**
     * 시작 비교연산자 필드
     */
    startOperatorField: Select = null;

    /**
     * 시작값 필드
     */
    startValueField: NumberField = null;

    /**
     * 종료 비교연산자 필드
     */
    endOperatorField: Select = null;

    /**
     * 종료값 필드
     */
    endValueField: NumberField = null;

    /**
     * 숫자 필터를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties) {
        super(properties);

        this.is_equal = properties?.is_equal === true;
        this.spinner = properties?.spinner === true;
        this.step = properties?.step ?? 1;
        this.format = properties?.format === true;
    }

    /**
     * 필터필드를 가져온다.
     *
     * @returns 필드
     */
    getField(): Container {
        if (this.field === null) {
            this.field = new Container({
                direction: 'column',
                items: [
                    this.#getOperatorField(),
                    this.#getValueField(),
                    this.#getStartOperatorField(),
                    this.#getStartValueField(),
                    this.#getEndOperatorField(),
                    this.#getEndValueField(),
                ],
            });
        }
        return this.field as Container;
    }

    /**
     * 필터객체를 가져온다.
     *
     * @returns 필터객체
     */
    async getFilter(): Promise<{ value: any; operator: string } | null> {
        const operator = this.is_equal == true ? '=' : ((await this.#getOperatorField().getValue()) as string);

        if (operator == 'range') {
            const startValue = await this.#getStartValueField().getValue();
            const endValue = await this.#getEndValueField().getValue();

            if (startValue === null || endValue === null) {
                return null;
            }

            return {
                value: {
                    start: { value: startValue, operator: await this.#getStartOperatorField().getValue() },
                    end: { value: endValue, operator: await this.#getEndOperatorField().getValue() },
                },
                operator: 'range',
            };
        }

        const value = await this.#getValueField().getValue();
        if (value === null) {
            return null;
        }

        return { value: value, operator: operator };
    }

    /**
     * 현재 필터설정을 필터필드에 적용한다.
     *
     * @param filter - 필터객체
     */
    async setFilter(filter: { value: any; operator: string } | null): Promise<void> {
        this.getButton().setDisabled(true);

        if (filter === null) {
            await this.#getOperatorField().setValue('=');
            await this.#getValueField().setValue(null);
            await this.#getStartValueField().setValue(null);
            await this.#getEndValueField().setValue(null);
        } else if (filter.operator == 'range') {
            await this.#getOperatorField().setValue('range');
            await this.#getStartOperatorField().setValue(filter.value?.start?.operator ?? '>=');
            await this.#getStartValueField().setValue(filter.value?.start?.value ?? null);
            await this.#getEndOperatorField().setValue(filter.value?.end?.operator ?? '<=');
            await this.#getEndValueField().setValue(filter.value?.end?.value ?? null);
        } else {
            await this.#getOperatorField().setValue(this.is_equal == true ? '=' : filter.operator);
            await this.#getValueField().setValue(filter.value ?? null);
        }

        this.getButton().setDisabled(false);
    }

    /**
     * 비교연산자 필드를 가져온다.
     *
     * @returns 비교연산자 필드
     */
    #getOperatorField(): Select {
        if (this.operatorField === null) {
            this.operatorField = new Select({
                hidden: this.is_equal,
                store: new Local({
                    provider: {
                        dataFields: ['value', 'display'],
                        loader: async () => [
                            { value: '=', display: 'equal' },
                            { value: '!=', display: 'not_equal' },
                            { value: '>=', display: 'gte' },
                            { value: '<=', display: 'lte' },
                            { value: '>', display: 'gt' },
                            { value: '<', display: 'lt' },
                            { value: 'range', display: 'range' },
                        ],
                    },
                }),
                value: '=',
                listRenderer: async (display) => {
                    return await Language.get('models.table.filter.number_operator.' + display).getText();
                },
                renderer: async (display) => {
                    return await Language.get('models.table.filter.number_operator.' + display).getText();
                },
                listeners: {
                    change: async (_field, value) => {
                        if (value == 'range') {
                            this.#getValueField().hide();
                            this.#getStartOperatorField().show();
                            this.#getStartValueField().show();
                            this.#getEndOperatorField().show();
                            this.#getEndValueField().show();
                        } else {
                            this.#getValueField().show();
                            this.#getStartOperatorField().hide();
                            this.#getStartValueField().hide();
                            this.#getEndOperatorField().hide();
                            this.#getEndValueField().hide();
                        }
                    },
                },
            });
        }
        return this.operatorField;
    }

    /**
     * 값 필드를 가져온다.
     *
     * @returns 값 필드
     */
    #getValueField(): NumberField {
        if (this.valueField === null) {
            this.valueField = new NumberField({
                spinner: this.spinner,
                step: this.step,
                format: this.format,
                listeners: {
                    keyboard: async (e) => {
                        if (e.key == 'Enter') {
                            this.getButton().onClick();
                        }
                    },
                },
            });
        }
        return this.valueField;
    }

    /**
     * 시작 비교연산자 필드를 가져온다.
     *
     * @returns 시작 비교연산자 필드
     */
    #getStartOperatorField(): Select {
        if (this.startOperatorField === null) {
            this.startOperatorField = new Select({
                hidden: true,
                store: new Local({
                    provider: {
                        dataFields: ['value', 'display'],
                        loader: async () => [
                            { value: '>=', display: 'gte' },
                            { value: '>', display: 'gt' },
                        ],
                    },
                }),
                value: '>=',
                listRenderer: async (display) => {
                    return await Language.get('models.table.filter.number_operator.' + display).getText();
                },
                renderer: async (display) => {
                    return await Language.get('models.table.filter.number_operator.' + display).getText();
                },
            });
        }
        return this.startOperatorField;
    }

    /**
     * 시작값 필드를 가져온다.
     *
     * @returns 시작값 필드
     */
    #getStartValueField(): NumberField {
        if (this.startValueField === null) {
            this.startValueField = new NumberField({
                hidden: true,
                spinner: this.spinner,
                step: this.step,
                format: this.format,
            });
        }
        return this.startValueField;
    }

    /**
     * 종료 비교연산자 필드를 가져온다.
     *
     * @returns 종료 비교연산자 필드
     */
    #getEndOperatorField(): Select {
        if (this.endOperatorField === null) {
            this.endOperatorField = new Select({
                hidden: true,
                store: new Local({
                    provider: {
                        dataFields: ['value', 'display'],
                        loader: async () => [
                            { value: '<=', display: 'lte' },
                            { value: '<', display: 'lt' },
                        ],
                    },
                }),
                value: '<=',
                listRenderer: async (display) => {
                    return await Language.get('models.table.filter.number_operator.' + display).getText();
                },
                renderer: async (display) => {
                    return await Language.get('models.table.filter.number_operator.' + display).getText();
                },
            });
        }
        return this.endOperatorField;
    }

    /**
     * 종료값 필드를 가져온다.
     *
     * @returns 종료값 필드
     */
    #getEndValueField(): NumberField {
        if (this.endValueField === null) {
            this.endValueField = new NumberField({
                hidden: true,
                spinner: this.spinner,
                step: this.step,
                format: this.format,
            });
        }
        return this.endValueField;
    }
}
